import { useEffect, useState } from 'react';
import { message } from 'antd';

import { createSubUser, updateSubUser } from 'services/sub-users';
import { queryClient } from 'config/query-client';
import { NewSubUserCustomError } from 'types';
import { SUB_USERS } from 'events/types';

import { Input } from './styled-components/common-components';
import useTranslation from '../../customHooks/translate';
import CustomDialog from './CustomDialog';

const initialValues: {
  fullName: string;
  email: string;
  id?: number;
} = {
  fullName: '',
  email: '',
};

interface IProps {
  open: boolean;
  handleClose: () => void;
}

const AddNewSubUserForm = ({ open, handleClose }: IProps) => {
  const [subUser, setSubUser] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [fullNameError, setfullNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const translation = useTranslation();

  useEffect(() => {
    setfullNameError(null);
    setEmailError(null);
    setSubUser(initialValues);
  }, [open]);

  const setOnValueChange = (e, propName, isSelect = false) => {
    e.persist && e.persist();
    setSubUser((prevState) => ({
      ...prevState,
      [propName]: isSelect ? e : e.target.value,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    const method = subUser.id ? updateSubUser : createSubUser;
    method(subUser)
      .then(() => {
        message.success(`${translation.messages.successAdded}`);
        queryClient.invalidateQueries({ queryKey: [SUB_USERS] });
        setSubUser(initialValues);
        handleClose();
      })

      .catch((error: NewSubUserCustomError) => {
        error?.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const errorMessagefullName = error.response?.data.errors
          ?.FullName?.[0] as string | undefined;

        const errorMessageEmail = error.response?.data.errors?.Email?.[0] as
          | string
          | undefined;

        setfullNameError(errorMessagefullName || null);

        setEmailError(errorMessageEmail || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      title={translation.subusers.NewSubUser}
      buttonTitle={translation.subusers.CreateSubUser}
      isLoading={loading}
      onSubmit={handleSave}
    >
      <form className="flex flex-col gap-[20px]">
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="fullName"
          >
            {translation.subusers.Name} *
          </label>
          <Input
            className="d-block"
            type="text"
            onChange={(event) => setOnValueChange(event, 'fullName')}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder="Elon"
            value={subUser.fullName || ''}
            style={{
              borderColor: fullNameError ? 'red' : '',
            }}
          />
          {fullNameError && (
            <span className="text-[12px] text-red-600">{fullNameError}</span>
          )}
        </div>

        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="email"
          >
            {translation.creditorAdd.Email} *
          </label>
          <Input
            className="d-block"
            type="email"
            maxLength={50}
            onChange={(event) => setOnValueChange(event, 'email')}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder="elon.musk@gmail.com"
            value={subUser.email || ''}
            style={{
              borderColor: emailError ? 'red' : '',
            }}
          />
          {emailError && (
            <span className="text-[12px] text-red-600">{emailError}</span>
          )}
        </div>
      </form>
    </CustomDialog>
  );
};

export default AddNewSubUserForm;
